<template>
  <header class="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
    <!-- Left: Logo and Navigation -->
    <div class="flex items-center space-x-4">
      <RouterLink to="/" class="flex items-center space-x-2">
        <div class="w-8 h-8 bg-admin-primary rounded flex items-center justify-center">
          <span class="text-white font-bold text-sm">A+</span>
        </div>
        <span class="font-semibold text-gray-900">AannemerPlus Admin</span>
      </RouterLink>
      
      <!-- Breadcrumbs -->
      <div v-if="currentProject" class="flex items-center space-x-2 text-sm text-gray-600">
        <span>/</span>
        <RouterLink :to="`/`" class="hover:text-admin-primary">
          {{ currentProject.name }}
        </RouterLink>
        <span v-if="currentPage">/</span>
        <div v-if="currentPage" class="flex items-center space-x-2">
          <span class="text-gray-900">{{ currentPage.title }}</span>
          <!-- Edit Page Button -->
          <Button
            icon="pi pi-cog"
            size="small"
            outlined
            class="text-gray-700 hover:text-admin-primary border-gray-300 ml-2"
            @click="showPageSettings = true"
            title="Edit Page Settings"
          />
        </div>
      </div>
    </div>
    
    <!-- Center: Page Actions -->
    <div v-if="currentPage" class="flex items-center space-x-2">
      <Button 
        icon="pi pi-eye" 
        label="Preview"
        outlined
        size="small"
        @click="$emit('preview')"
      />
      
      <Button 
        icon="pi pi-save" 
        label="Save"
        :loading="isSaving"
        :disabled="!hasUnsavedChanges"
        size="small"
        @click="$emit('save')"
      />
      
      <Button 
        icon="pi pi-upload" 
        label="Publish"
        severity="success"
        :loading="isPublishing"
        size="small"
        @click="$emit('publish')"
      />
    </div>
    
    <!-- Right: User Menu -->
    <div class="flex items-center space-x-4">
      <!-- Status Indicator -->
      <div v-if="hasUnsavedChanges" class="flex items-center space-x-2 text-sm">
        <div class="w-2 h-2 bg-orange-400 rounded-full"></div>
        <span class="text-gray-600">Unsaved changes</span>
      </div>
      
      <!-- User Avatar/Menu -->
      <div class="flex items-center space-x-2">
        <span v-if="authStore.user" class="text-sm text-gray-700">
          {{ authStore.user.firstName }}
        </span>
        <RouterLink to="/settings">
          <Button
            icon="pi pi-cog"
            size="small"
            text
            rounded
            severity="secondary"
            title="Settings"
          />
        </RouterLink>
        <Button
          icon="pi pi-sign-out"
          size="small"
          text
          rounded
          severity="secondary"
          title="Logout"
          @click="authStore.logout()"
        />
      </div>
    </div>

    <!-- Page Settings Modal -->
    <PageSettingsModal
      v-if="currentPage"
      v-model:visible="showPageSettings"
      :page="currentPage"
      @update="handlePageUpdate"
    />
  </header>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import Button from 'primevue/button'
import PageSettingsModal from '../admin/PageSettingsModal.vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

interface Props {
  currentProject?: any
  currentPage?: any
  hasUnsavedChanges?: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  save: []
  publish: []
  preview: []
  'edit-page': [pageData: any]
}>()

const isSaving = ref(false)
const isPublishing = ref(false)
const showPageSettings = ref(false)


// Handle page settings update
const handlePageUpdate = (pageData: any) => {
  showPageSettings.value = false
  // Emit to parent to handle the actual update
  emit('edit-page', pageData)
}
</script>